import {
  COMMERCIAL_CATEGORIES,
  userIsRestricted,
} from '../hooks/useCommercialAccess';

/**
 * sessionStorage key carrying the "we rewrote your saved search" notice across
 * the full document load that opening a saved search performs. Written by the
 * saved-searches list, read and cleared once by the search page.
 */
export const REMOVED_COMMERCIAL_FILTERS_KEY = 'removedCommercialFilters';

/**
 * Filter out commercial categories from saved search query if user is restricted
 * Returns both the filtered query and a boolean indicating if filters were removed
 */
export const filterSavedSearchQuery = (queryObject, memData) => {
  // Only rewrite a saved search once we actually know the plan — otherwise a
  // Legend customer whose memData has not landed yet gets their commercial
  // filters stripped and a "filters were removed" toast they never earned.
  if (!userIsRestricted(memData) || !queryObject?.property_type) {
    return { query: queryObject, hasCommercialFilters: false };
  }

  // Parse property_type (could be string or array)
  // Both branches normalise: a saved array can carry ' Office ' just as a saved
  // string can, and an untrimmed entry misses the commercial check entirely —
  // the restricted member keeps the filter and gets no removal notice.
  let propertyTypes = (Array.isArray(queryObject.property_type)
    ? queryObject.property_type
    : (queryObject.property_type || '').split(','))
    .map(s => String(s ?? '').trim())
    .filter(Boolean);

  const commercialCategoriesLower = COMMERCIAL_CATEGORIES.map(c => c.toLowerCase());
  const hasCommercial = propertyTypes.some(type =>
    commercialCategoriesLower.includes(type.toLowerCase())
  );

  if (!hasCommercial) {
    return { query: queryObject, hasCommercialFilters: false };
  }

  // Filter out commercial categories
  const filteredTypes = propertyTypes.filter(type =>
    !commercialCategoriesLower.includes(type.toLowerCase())
  );

  // Return filtered query
  const filteredQuery = {
    ...queryObject,
    property_type: filteredTypes.length > 0 ? filteredTypes.join(',') : undefined
  };

  // Remove empty property_type key
  if (!filteredQuery.property_type) {
    delete filteredQuery.property_type;
  }

  return {
    query: filteredQuery,
    hasCommercialFilters: true,
    removedFilters: propertyTypes.filter(type =>
      commercialCategoriesLower.includes(type.toLowerCase())
    )
  };
};
